import { callApi, ThrowError } from '@/api/callApi';
import {
  API_ENDPOINT,
  API_URL_ORDER_SERVICE,
  API_URL_REPORT_SERVICE,
} from '@/api/constant';
import PageHeading from '@/components/common/PageHeading';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import BarGraph from '@/components/UI/Chart/BarGraph';
import AreaGrowthGraph from '@/components/UI/Chart/LineChart';
import PiChart from '@/components/UI/Chart/PiChart';
import DateRangePicker from '@/components/UI/DateRange';
import FormField from '@/components/UI/FormField';
import DashboardSkeleton from '@/components/UI/loaders/CommonSkeleton';
import { IReportOverView } from '@/interfaces/reportOverView.interface';
import {
  formatDateTime,
  formatPlaceholder,
  GetFilterUrl,
  OrderStatus,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Range } from 'react-date-range';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<IReportOverView | null>(null);
  const [selectedValue, setSelectedValue] = useState('week');
  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const filterTypes = [
    { label: 'Custom', value: 'custom' },
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
  ];

  const fetchDashboardData = async () => {
    try {
      if (!dateRange?.[0]?.startDate || !dateRange?.[0]?.endDate) {
        return;
      }
      setLoading(true);
      const startDate =
        selectedValue == 'custom' || selectedValue === ''
          ? formatDateTime(dateRange[0].startDate, 'YYMMDD')
          : '';
      const endDate =
        selectedValue == 'custom' || selectedValue === ''
          ? formatDateTime(dateRange[0].endDate, 'YYMMDD')
          : '';
      const filterQuery = {
        from: startDate,
        to: endDate,
        type: selectedValue,
      };

      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_REPORT_SERVICE}${API_ENDPOINT.dashboard}/report?${query ? query : ''}`;
      const res = await callApi<IReportOverView>(PATH, { method: 'GET' });
      setLoading(false);
      setReportData(res);
    } catch (error) {
      ThrowError(error);
      setLoading(false);
    }
  };

  const getDateRangeByType = (type: string): Range => {
    const now = new Date();
    let startDate = new Date();
    let endDate = now;

    switch (type) {
      case 'day':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const firstDayOfWeek = now.getDate() - now.getDay();
        startDate = new Date(now.getFullYear(), now.getMonth(), firstDayOfWeek);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
      default:
        return dateRange[0];
    }

    return { startDate, endDate, key: 'selection' };
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedValue]);

  const deviceTypeCards =
    reportData?.order?.analytics?.deviceType
      ?.map((item) => {
        if (item.name === 'Android') {
          return {
            deviceType: item.name,
            title: 'Android App Orders',
            value: item.count || 0,
            percent: `BHD ${toFixedThree(item.sales)}`,
            subtitle: 'Total Sales',
            percentColor: 'text-green-500',
            img: '/assets/images/dashboard-card-img-7.svg',
            to: RoutesLink.order,
            status: null,
          };
        }

        if (item.name === 'Ios') {
          return {
            deviceType: item.name,
            title: 'iOS App Orders',
            value: item.count || 0,
            percent: `BHD ${toFixedThree(item.sales)}`,
            subtitle: 'Total Sales',
            percentColor: 'text-green-500',
            img: '/assets/images/dashboard-card-img-8.svg',
            to: RoutesLink.order,
            status: null,
          };
        }

        return null;
      })
      .filter(Boolean) ?? [];

  const dashboardCards = [
    {
      deviceType: null,
      title: 'Total Sales',
      value: `BD ${toFixedThree(reportData?.order?.analytics?.totalSales?.amount || 0)}`,
      subtitle: formatPlaceholder(
        reportData?.order?.analytics?.totalSales?.type,
      ),
      percent: `${reportData?.order?.analytics?.totalSales?.comparison?.change || 0}%`,
      percentColor:
        reportData?.order?.analytics?.totalSales?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: '/assets/images/dashboard-card-img-1.svg',
      to: RoutesLink.order,
      status: OrderStatus.DELIVERED,
    },
    {
      deviceType: null,
      title: 'Total Refund Amount',
      value: `BD ${toFixedThree(reportData?.order?.analytics?.refundAmount?.amount || 0)}`,
      subtitle: formatPlaceholder(
        reportData?.order?.analytics?.refundAmount?.type,
      ),
      percent: `${reportData?.order?.analytics?.refundAmount?.comparison?.change || 0}%`,
      percentColor:
        reportData?.order?.analytics?.refundAmount?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: '/assets/images/dashboard-card-img-2.svg',
      to: RoutesLink.refundReturn,
      status: OrderStatus.REFUNDED,
    },
    {
      deviceType: null,
      title: 'Total Orders',
      value: `${reportData?.order?.analytics?.totalOrders?.amount || 0}`,
      subtitle: formatPlaceholder(
        reportData?.order?.analytics?.totalOrders?.type,
      ),
      percent: `${reportData?.order?.analytics?.totalOrders?.comparison?.change || 0}%`,
      percentColor:
        reportData?.order?.analytics?.totalOrders?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: '/assets/images/dashboard-card-img-3.svg',
      to: RoutesLink.order,
      status: null,
    },
    {
      deviceType: null,
      title: 'Total Amount Paid By Wallet',
      value: `BD ${toFixedThree(reportData?.order?.analytics?.paidByWallet?.amount || 0)}`,
      subtitle: formatPlaceholder(
        reportData?.order?.analytics?.paidByWallet?.type,
      ),
      percent: `${reportData?.order?.analytics?.paidByWallet?.comparison?.change || 0}%`,
      percentColor:
        reportData?.order?.analytics?.paidByWallet?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: '/assets/images/dashboard-card-img-4.svg',
      to: RoutesLink.order,
      status: null,
    },
    ...deviceTypeCards,
  ];

  const downloadReport = async (
    status: OrderStatus | null,
    paidByWallet: boolean | null = null,
    deviceType: string | null = null,
  ) => {
    try {
      const range =
        selectedValue === 'custom'
          ? dateRange[0]
          : getDateRangeByType(selectedValue);

      const startDate = moment(range.startDate).format('YYYY-MM-DD');
      const endDate = moment(range.endDate).format('YYYY-MM-DD');

      const filterQuery = {
        startDate: startDate,
        endDate: endDate,
        status: status,
        paidByWallet: paidByWallet,
        deviceType: deviceType,
      };
      const query = GetFilterUrl(filterQuery);

      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/download-report?${query ? query : ''}`;
      const response = await callApi(PATH, {
        method: 'POST',
        responseType: 'arraybuffer',
      });
      if (response instanceof ArrayBuffer) {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'report.xlsx';
        link.click();

        URL.revokeObjectURL(link.href);
      } else {
        showToastNotification('danger', 'Failed to download the report');
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  const filterValues = [
    'Picked up',
    'In progress',
    'Delayed',
    'Refund in progress',
    'Completed',
  ];
  
  const filteredOrders = reportData?.order.graphs.orderStatus.filter(
    (order) => !filterValues.includes(order.name as OrderStatus),
  );

  return (
    <Layout>
      {loading ? (
        <>
          <DashboardSkeleton />
        </>
      ) : (
        <>
          <div className="flex items-center">
            <PageHeading text="Report Overview" />
            <div className="flex items-center gap-2 mb-6">
              {(selectedValue === 'custom' || selectedValue === '') && (
                <>
                  <DateRangePicker
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={fetchDashboardData}
                  >
                    Apply Filter
                  </button>
                </>
              )}
              <FormField
                type="select"
                isHidden
                options={filterTypes}
                value={selectedValue}
                onChange={(e) => setSelectedValue(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
            {dashboardCards.map((card) => {
              return (
                card && (
                  <div
                    key={card.title}
                    className={`bg-white rounded-tl-[26px] rounded-bl-[26px] flex items-center justify-between overflow-hidden shadow-1}`}
                  >
                    <div className="p-6">
                      <h3 className="text-black text-sm mb-4">{card.title}</h3>
                      <h2 className="text-[17px] text-black mb-2">
                        {card.value}
                      </h2>
                      <p className="text-xs text-gray-100">
                        {card.subtitle}
                        <span
                          className={`ml-1 text-sm font-medium ${card.percentColor}`}
                        >
                          {card.percent || 0}
                        </span>
                      </p>
                      {card.value !== `BD ${toFixedThree(0)}` && (
                        <button
                          onClick={() =>
                            downloadReport(
                              card.status,
                              card.title === 'Total Amount Paid By Wallet'
                                ? true
                                : null,
                              card.deviceType,
                            )
                          }
                          className="btn-secondary mt-2 text-nowrap text-sm! p-2!"
                        >
                          Download Report
                        </button>
                      )}
                    </div>
                    <img
                      className="h-full -mr-0.5"
                      src={card.img}
                      alt={card.title}
                    />
                  </div>
                )
              );
            })}
          </div>
          <PageHeading text="Sales Analytics" />
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[1.8fr_1fr] gap-5 mb-5">
            <Card>
              <PageHeading text="Order Analytics" />
              <BarGraph data={filteredOrders} />
            </Card>
            <Card className="max-h-fit">
              <PageHeading text="Orders By Category" />
              <PiChart
                data={
                  reportData?.order?.graphs?.salesByCategory?.map((item) => ({
                    name: item?.name,
                    revenue: item?.count,
                  })) || []
                }
              />
            </Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <Card>
              <PageHeading text="Refund Analytics" />
              <AreaGrowthGraph
                data={reportData?.order?.graphs?.refundGraph || []}
              />
            </Card>
            <Card>
              <PageHeading text="Redeem Points" />
              <BarGraph
                name="Points"
                data={
                  reportData?.loyalty?.graphs?.pointsRedeemedGraph.map((i) => ({
                    name: i.name,
                    count: i.points,
                  })) || []
                }
              />
            </Card>
          </div>
          <Card>
            <PageHeading text="Revenue Analytics" />
            <AreaGrowthGraph
              data={reportData?.order?.graphs?.revenueGrowth || []}
            />
          </Card>
        </>
      )}
    </Layout>
  );
};

export default Reports;
